import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, LoginRequest } from '../types';
import apiService from '../services/api';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: LoginRequest) => Promise<void>;
  logout: () => void;
  updateUser: (user: User) => void;
  isSuperAdmin: () => boolean;
  isResponsable: () => boolean;
  canModifyPonderation: () => boolean;
  canAccessStructure: (structureId: string) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      const storedUser = apiService.getStoredUser();
      if (storedUser && apiService.isAuthenticated()) {
        try {
          const currentUser = await apiService.getCurrentUser();
          setUser(currentUser);
          localStorage.setItem('user', JSON.stringify(currentUser));
        } catch (error) {
          apiService.logout();
        }
      }
      setIsLoading(false);
    };

    initAuth();
  }, []);

  const login = async (credentials: LoginRequest) => {
    const response = await apiService.login(credentials);
    setUser(response.user);
  };

  const logout = () => {
    setUser(null);
    apiService.logout();
  };

  const updateUser = (updated: User) => {
    setUser(updated);
    localStorage.setItem('user', JSON.stringify(updated));
  };

  const isSuperAdmin = () => user?.role === 'SUPER_ADMIN';
  const isResponsable = () => user?.role === 'RESPONSABLE';
  
  const canModifyPonderation = () => user?.role === 'SUPER_ADMIN' || user?.role === 'RESPONSABLE';
  
  const canAccessStructure = (structureId: string) => {
    if (user?.role === 'SUPER_ADMIN') return true;
    return user?.structureId === structureId;
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        logout,
        updateUser,
        isSuperAdmin,
        isResponsable,
        canModifyPonderation,
        canAccessStructure,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
